import React from "react";
import { Sparkles, Check } from "lucide-react";
import WaitlistForm from "./WaitlistForm";

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative pt-32 lg:pt-40 pb-24 lg:pb-32 overflow-hidden">
      {/* Background blurs */}
      <div aria-hidden="true" className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-bhyyve-honey/[0.08] blur-3xl" />
      <div aria-hidden="true" className="absolute top-1/3 -right-32 w-[500px] h-[500px] rounded-full bg-bhyyve-honey/[0.12] blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-12 items-center">
          {/* Left content */}
          <div className="col-span-full lg:col-span-7 flex flex-col gap-8 animate-fade-up">
            {/* Eyebrow badge */}
            <div data-testid="hero-eyebrow" className="inline-flex items-center gap-2 self-start px-5 py-2 rounded-full bg-bhyyve-honey/[0.12] border border-bhyyve-honey/30 text-xs font-semibold tracking-wider uppercase text-bhyyve-honeyDeep">
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
              Founding Members · Early Access
            </div>

            {/* Headline */}
            <h1 data-testid="hero-headline" className="font-heading text-6xl sm:text-7xl lg:text-8xl font-medium tracking-tight leading-[1.02] text-bhyyve-ink">
              Skip the Noise.{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-bhyyve-honeyDeep italic font-normal">Get First Access</span>
                <span aria-hidden="true" className="absolute left-0 right-0 bottom-2 h-3.5 bg-bhyyve-honey/35 -z-0 rounded-sm" />
              </span>{" "}
              to bHyyve.
            </h1>

            {/* Subheadline */}
            <p data-testid="hero-subheadline" className="text-xl sm:text-2xl leading-relaxed text-bhyyve-muted max-w-xl">
              A smarter way to order services and products — seamless, trusted, built for you.
            </p>

            {/* Form */}
            <div id="waitlist" className="pt-4">
              <WaitlistForm variant="hero" />
            </div>

            {/* Microcopy */}
            <p data-testid="hero-microcopy" className="text-base font-medium text-bhyyve-muted">
              Join now to unlock exclusive Founding Member rewards.
            </p>

            {/* Trust indicators */}
            <ul data-testid="hero-trust-indicators" className="flex flex-wrap gap-x-8 gap-y-3 pt-2">
              {["No spam", "Early access", "Limited spots"].map((item) => (
                <li key={item} className="inline-flex items-center gap-2.5 text-base font-medium text-bhyyve-muted">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-bhyyve-honey/20">
                    <Check className="w-3 h-3 text-bhyyve-honeyDeep" strokeWidth={3} aria-hidden="true" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Right - Brand showcase */}
          <div className="col-span-full lg:col-span-5 relative animate-fade-in">
            <div
              data-testid="hero-brand-showcase"
              className="relative aspect-[4/5] lg:aspect-[3/4] rounded-[2.5rem] overflow-hidden border border-bhyyve-border shadow-[0_30px_80px_-20px_rgba(122,79,0,0.25)]"
              style={{
                background: "radial-gradient(120% 90% at 50% 30%, rgb(255, 252, 243) 0%, rgb(251, 233, 181) 55%, rgb(244, 180, 0) 100%)",
              }}
            >
              {/* Honeycomb pattern overlay */}
              <div
                aria-hidden="true"
                className="absolute inset-0 opacity-90 mix-blend-multiply"
                style={{
                  backgroundImage: `url("data:image/svg+xml;utf8,%3Csvg%20xmlns%3D'http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg'%20viewBox%3D'0%200%2056%20100'%20width%3D'56'%20height%3D'100'%3E%20%20%3Cg%20fill%3D'none'%20stroke%3D'%2523F4B400'%20stroke-opacity%3D'0.18'%20stroke-width%3D'1.2'%3E%20%20%20%20%3Cpolygon%20points%3D'28%2C2%2054%2C17%2054%2C47%2028%2C62%202%2C47%202%2C17'%2F%3E%20%20%20%20%3Cpolygon%20points%3D'0%2C52%2026%2C67%2026%2C97%200%2C112%20-26%2C97%20-26%2C67'%2F%3E%20%20%20%20%3Cpolygon%20points%3D'56%2C52%2082%2C67%2082%2C97%2056%2C112%2030%2C97%2030%2C67'%2F%3E%20%20%3C%2Fg%3E%3C%2Fsvg%3E")`,
                  backgroundSize: "112px 200px",
                }}
              />
              {/* Center glow */}
              <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center">
                <div className="w-[70%] aspect-square rounded-full bg-white/60 blur-3xl" />
              </div>
              {/* Logo + text */}
              <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
                <img
                  alt="bHyyve"
                  className="w-52 sm:w-60 lg:w-64 h-auto drop-shadow-[0_10px_30px_rgba(122,79,0,0.25)] animate-float-y"
                  loading="eager"
                  src="https://customer-assets.emergentagent.com/job_site-creator-2220/artifacts/g859gcm8_bHyyve%20Icon.png"
                />
                <div className="mt-6 font-heading text-5xl sm:text-6xl font-bold tracking-tight text-bhyyve-charcoal">bHyyve</div>
                <div className="mt-3 text-sm sm:text-base tracking-[0.18em] uppercase font-semibold text-bhyyve-honeyDark">Engineered for Trust</div>
              </div>
              {/* Bottom card */}
              <div className="absolute bottom-6 left-6 right-6 sm:left-auto sm:right-6 sm:max-w-[280px] bg-white/95 backdrop-blur-md border border-white/70 rounded-2xl p-5 shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-bhyyve-charcoal flex items-center justify-center shrink-0">
                    <Sparkles className="w-6 h-6 text-bhyyve-honey" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-bhyyve-honeyDeep">Founding Member</div>
                    <div className="text-base font-semibold text-bhyyve-ink">$100 wallet credit unlocked</div>
                  </div>
                </div>
              </div>
            </div>
            {/* Floating stat badge */}
            <div data-testid="hero-stat-badge" className="absolute -top-5 -left-5 sm:-left-10 bg-white border border-bhyyve-border rounded-2xl px-6 py-4 shadow-lg hidden sm:block">
              <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-bhyyve-honeyDeep">Limited Gifts</div>
              <div className="font-heading text-3xl font-bold text-bhyyve-ink leading-tight">First 500 signups</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
